import { DocumentNode, gql } from '@apollo/client';
import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { MembershipStatus } from '@/graphql-types/globalTypes';
import {
  ClockIcon,
  MoreHorizontalIcon,
  PlusCircleIcon,
} from '@/imports/ui/chakra/feather';

import { MembershipActionsMenu_MembershipDataFragment } from './graphql-types/MembershipActionsMenu_MembershipDataFragment';

export const MembershipActionsMenu: React.FC<{
  membershipData: MembershipActionsMenu_MembershipDataFragment;
  userId?: string;
  organizationId?: string;
  organizationIsOwnerless?: boolean;
  shouldBeAdminView: boolean;
}> & { membershipDataFragment: DocumentNode } = ({
  membershipData,
  userId,
  organizationId,
  organizationIsOwnerless,
  shouldBeAdminView,
}) => {
  return (
    <Box>
      <Menu>
        <MenuButton
          size="sm"
          as={IconButton}
          variant="outline"
          colorScheme="gray"
          aria-label="membership actions"
          icon={<MoreHorizontalIcon />}
        />
        <MenuList>
          {!!membershipData.canCurrentUserAddMembership && (
            <>
              {!!userId && (
                <span>
                  <NextLink
                    href={
                      shouldBeAdminView
                        ? `/admin/membership/people/${userId}/membership/new`
                        : '/memberships'
                    }
                  >
                    <MenuItem icon={<PlusCircleIcon />}>
                      {shouldBeAdminView
                        ? 'Add a Membership'
                        : membershipData.membershipStatus ===
                          MembershipStatus.MEMBER
                        ? 'Switch Membership'
                        : 'Add a Membership'}
                    </MenuItem>
                  </NextLink>
                </span>
              )}

              {!!organizationId && (
                <Tooltip
                  label="A company must have at least one Account Owner before it can receive membership"
                  isDisabled={!organizationIsOwnerless}
                >
                  <span>
                    <NextLink
                      href={
                        shouldBeAdminView
                          ? `/admin/companies/${organizationId}/membership/new`
                          : '/memberships'
                      }
                    >
                      <MenuItem
                        icon={<PlusCircleIcon />}
                        isDisabled={organizationIsOwnerless}
                      >
                        {shouldBeAdminView
                          ? 'Add a Membership'
                          : membershipData.membershipStatus ===
                            MembershipStatus.MEMBER
                          ? 'Switch Membership'
                          : 'Add a Membership'}
                      </MenuItem>
                    </NextLink>
                  </span>
                </Tooltip>
              )}
            </>
          )}

          {!shouldBeAdminView &&
            !membershipData.canCurrentUserAddMembership && (
              <span>
                <NextLink href={`/memberships`}>
                  <MenuItem icon={<PlusCircleIcon />}>
                    Switch Membership
                  </MenuItem>
                </NextLink>
              </span>
            )}

          <span>
            <NextLink
              href={
                shouldBeAdminView
                  ? userId
                    ? `/admin/membership/people/${userId}/membership/history`
                    : `/admin/companies/${organizationId}/membership/history`
                  : userId
                  ? '/me/membership/history'
                  : '/my-company/membership/history'
              }
            >
              <MenuItem icon={<ClockIcon />}>View Membership History</MenuItem>
            </NextLink>
          </span>
        </MenuList>
      </Menu>
    </Box>
  );
};

MembershipActionsMenu.membershipDataFragment = gql`
  fragment MembershipActionsMenu_MembershipDataFragment on MembershipData {
    canCurrentUserAddMembership
    membershipStatus
  }
`;
